/**
 * Service for managing college notes
 */

import { buildApiUrl } from '../utils/apiConfig';
import { jsonSend } from '../utils/http';
import { CreateCollegeNoteRequest, CollegeNoteResponse } from '../types/collegeNotes';

/**
 * Fetches all notes for a specific college and athlete
 * @param athleteId - The athlete's ID
 * @param collegeId - The college's ID
 * @returns Promise<CollegeNoteResponse[]> - Array of notes
 */
export const fetchCollegeNotes = async (
  athleteId: number,
  collegeId: number
): Promise<CollegeNoteResponse[]> => {
  const url = buildApiUrl(`/api/athletes/${athleteId}/colleges/${collegeId}/notes`);

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  });

  if (response.status === 401) {
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  if (response.status === 404) {
    throw new Error('Athlete not found');
  }

  if (!response.ok) {
    throw new Error('Failed to fetch notes');
  }

  return await response.json();
};

/**
 * Creates a new college note for an athlete
 * @param athleteId - The athlete's ID
 * @param collegeId - The college's ID
 * @param name - The note name/title
 * @param body - The note body text
 * @returns Promise<CollegeNoteResponse> - The created note
 */
export const createCollegeNote = async (
  athleteId: number,
  collegeId: number,
  name: string,
  body: string
): Promise<CollegeNoteResponse> => {
  if (!name || !name.trim()) {
    throw new Error('Note name cannot be empty');
  }

  if (!body || !body.trim()) {
    throw new Error('Note body cannot be empty');
  }

  const url = buildApiUrl(`/api/athletes/${athleteId}/colleges/${collegeId}/notes`);
  const request: CreateCollegeNoteRequest = {
    name: name.trim(),
    body: body.trim()
  };

  return await jsonSend<CollegeNoteResponse>(url, 'POST', request, true);
};

/**
 * Deletes a college note for an athlete
 * @param athleteId - The athlete's ID
 * @param noteId - The note's ID to delete
 * @returns Promise<void>
 */
export const deleteCollegeNote = async (
  athleteId: number,
  noteId: number
): Promise<void> => {
  const url = buildApiUrl(`/api/athletes/${athleteId}/college/notes/${noteId}`);

  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  });

  if (response.status === 401) {
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  if (response.status === 404) {
    throw new Error('Note not found or does not belong to this athlete');
  }

  if (!response.ok) {
    throw new Error('Failed to delete note');
  }

  // 204 No Content - successful deletion
};

/**
 * Updates an existing college note.
 */
export const updateCollegeNote = async (
  athleteId: number,
  noteId: number,
  name: string,
  body: string
): Promise<CollegeNoteResponse> => {
  if (!name || !name.trim()) {
    throw new Error('Note name cannot be empty');
  }
  if (!body || !body.trim()) {
    throw new Error('Note body cannot be empty');
  }

  const url = buildApiUrl(`/api/athletes/${athleteId}/college/notes/${noteId}`);
  return await jsonSend<CollegeNoteResponse>(url, 'PUT', {
    name: name.trim(),
    body: body.trim()
  }, true);
};

